import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import liff from "../../../lib/liff";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import StyledButton from "works/chi/components/StyledButton";

import { COLORS } from "constants/colors";
import HowToUseSlider from "works/chi/components/HowToUseSlider";
import AppLayout from "works/chi/components/AppLayout";

import {
  getRequestFormUrl,
  getGuidelinesUrl,
  howToUseDetailUrls,
} from "constants/links";
import ButtonDialog from "default/components/ButtonDialog";
import {
  getGuidelinesAgreementStatus,
  postGuidelinesAgreement,
} from "utils/api";

function HowToUse() {
  const { t, i18n } = useTranslation(["chi"]);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  // クエリパラメータを取得
  const soulId = queryParams.get("soul_id");
  const lng = queryParams.get("lng") || "ja";
  const requestFormUrl = getRequestFormUrl(lng);
  const guidelineUrl = getGuidelinesUrl("chi", lng);

  const withLng = (path) => {
    const hasQuery = path.includes("?");
    return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
  };

  // ガイドラインダイアログ用state
  const [openGuidelineDialog, setOpenGuidelineDialog] = useState(false);
  const [agreeLoading, setAgreeLoading] = useState(false); // 追加

  const navigateToHowToUseDetail = async () => {
    window.open(howToUseDetailUrls["chi"][lng], "_blank");
  };

  const navigateToGuideLine = async () => {
    window.open(guidelineUrl, "_blank");
  };

  const handleCloseWindow = () => {
    liff.closeWindow();
  };

  const navigateToRequestForm = () => {
    if (lng === "th") {
      navigate(withLng(`/chi/request-form?soul_id=${soulId}`));
      return;
    }
    window.open(requestFormUrl, "_blank");
  };

  const navigateToContact = () => {
    window.open("https://line.me/R/ti/p/@314jdaww", "_blank");
  };

  // 初回ロード時
  useEffect(() => {
    const guidelineCheck = async () => {
      const status = await getGuidelinesAgreementStatus(soulId);
      if (status === false) {
        setOpenGuidelineDialog(true);
        return;
      }
    };

    guidelineCheck();
  }, []);

  // ガイドライン同意ボタン押下時の処理
  const handleAgreeGuideline = async () => {
    setAgreeLoading(true);
    try {
      await postGuidelinesAgreement(soulId);
      setOpenGuidelineDialog(false);
    } finally {
      setAgreeLoading(false);
    }
  };

  return (
    <AppLayout>
      <Box
        sx={{
          display: "flex",
          paddingTop: 4, // 任意で上マージンを追加
          paddingBottom: 4, // 任意で下マージンを追加
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            marginBottom: 4,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Noto Serif JP", serif',
              fontSize: "20px",
              fontWeight: "bold",
              color: COLORS.TEXT_WHITE,
            }}
          >
            {/* 使い方 */}
            {t("chi:HowToUse.Title")}
          </Typography>
        </Box>

        {/* ここにスワイプ可能なcomponentを入れる */}
        <HowToUseSlider />

        <StyledButton
          variant="contained"
          sx={{
            marginTop: 6,
          }}
          onClick={navigateToHowToUseDetail}
        >
          {/* 使い方詳細 */}
          {t("chi:HowToUse.Detail")}
        </StyledButton>

        <StyledButton
          variant="contained"
          sx={{
            marginTop: 4,
          }}
          onClick={navigateToGuideLine}
        >
          {/* ガイドライン */}
          {t("chi:HowToUse.GuideLines")}
        </StyledButton>

        <StyledButton
          variant="contained"
          sx={{
            marginTop: 4,
          }}
          onClick={handleCloseWindow}
        >
          {/* チャットに戻る */}
          {t("chi:button.back_to_chat")}
        </StyledButton>

        {requestFormUrl && (
          <StyledButton
            variant="contained"
            sx={{
              marginTop: 4,
            }}
            onClick={navigateToRequestForm}
          >
            {t("chi:button.request_form")}
          </StyledButton>
        )}

        <StyledButton
          variant="contained"
          sx={{
            marginTop: 4,
          }}
          onClick={navigateToContact}
        >
          {/* お問い合わせ */}
          {t("chi:HowToUse.Contact")}
        </StyledButton>

        {/* ガイドラインダイアログ */}
        <ButtonDialog
          open={openGuidelineDialog}
          title={t("chi:HowToUse.BeforeService")}
          showCloseButton={false}
          content={
            <>
              <div
                style={{
                  textAlign: "center",
                  marginBottom: 8,
                  fontSize: 15,
                  color: COLORS.TEXT_BLACK,
                  whiteSpace: "pre-line",
                }}
              >
                {t("chi:HowToUse.GuidelineNotice")}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 24,
                  marginBottom: 24,
                }}
              >
                <a
                  href={guidelineUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: COLORS.SKY_BLUE,
                    textDecoration: "none",
                    fontWeight: 500,
                    fontSize: 15,
                  }}
                >
                  {t("chi:Footer.guidelines")}
                </a>
              </div>
            </>
          }
          rawButtons={[
            <StyledButton
              variant="contained"
              sx={{
                width: "100%",
                fontSize: "17px",
                color: COLORS.TEXT_BLACK,
                border: `2px solid ${COLORS.TEXT_BLACK}`,
              }}
              onClick={handleAgreeGuideline}
              disabled={agreeLoading} // チェックボックス判定を削除
            >
              {t("chi:button.confirmed")}
            </StyledButton>,
          ]}
          handleClose={() => { }}
        />
      </Box>
    </AppLayout>
  );
}

export default HowToUse;
